import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, Injector, input, OnDestroy, runInInjectionContext, signal, viewChild, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, fromEvent, map } from 'rxjs';
import { SortField, SortValue } from '../../entities/sort/sort';
import { Reloadable } from '../../services/command/command.service';
import { TabView } from 'primeng/tabview';
import { RefetchCoordinator } from '../../services/refetch-coordinator/refetch-coordinator.service';
import { RolesService } from '../../services/roles/roles.service';
import { FilterStateService } from '../../services/filter-state/filter-state.service';
import { UsersService } from '../../services/users/users.service';
import { SidebarService } from '../../services/sidebar/sidebar.service';
import { Issue } from '../../entities/issue/issue';
import { IssueTypeService } from '../../services/issue/issue-type.service';
import { IssueStatusService } from '../../services/issue/issue-status.service';
import { FilterDateSelectValue } from '../../entities/filterDateSelect/filter-date-select-value';
import { ReactiveContainer } from '../../containers/reactive-container';
import { Label } from '../../entities/label/label';
import { IssueListFilterState, isValidIssueListFilterState, DEFAULT_ISSUE_LIST_FILTER_STATE } from './issue-list-filter-state.interface';
import { LocationService } from '../../services/location/location.service';
import * as fromGraphQl from './graphql';

@Component({
  selector: 'app-issue-list',
  templateUrl: './issue-list.component.html',
  styleUrls: ['./issue-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IssueListComponent implements Reloadable, OnDestroy {

  readonly sidebarService = inject(SidebarService);
  readonly rolesService = inject(RolesService);
  private readonly issueTypeService = inject(IssueTypeService);
  private readonly issueStatusService = inject(IssueStatusService);
  private readonly usersService = inject(UsersService);
  private readonly locationService = inject(LocationService);
  private readonly refetchCoordinator = inject(RefetchCoordinator);
  private readonly filterStateService = inject(FilterStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly targetEntityType = input<string>();
  readonly targetEntityId = input<string>();
  readonly showPageTitle = input(true);
  readonly showTabs = input(true);
  readonly filterStorageKey = input<string>();

  readonly filterState = signal<IssueListFilterState>(DEFAULT_ISSUE_LIST_FILTER_STATE);

  readonly selectedFilter = computed(() => this.filterState().tabIndex);
  readonly pageSize = computed(() => this.filterState().pageSize);
  readonly issueTypesModel = computed(() => this.filterState().issueTypeCodes);
  readonly issueStatusesModel = computed(() => this.filterState().issueStatusCodes);
  readonly assigneesModel = computed(() => this.filterState().assigneeIds);
  readonly locationsModel = computed(() => this.filterState().locationIds);
  readonly dueDateModel = computed(() => this.filterState().dueDate);
  readonly labelsModel = computed(() => this.filterState().labelIds);

  readonly binaryStatus = computed(() => {
    if (!this.showTabs()) return undefined;
    const index = this.filterState().tabIndex;
    if (index === 0) return 'open';
    if (index === 1) return 'closed';
    return undefined;
  });

  readonly sortModel = computed<SortValue>(() => {
    const sortState = this.filterState().sort;
    const field = this.sortFields.find(f => f.key === sortState.key) ?? this.sortFields[0];
    return { field, direction: sortState.direction ?? 'desc' };
  });

  readonly pageNumber = signal(0);

  readonly sortFields: SortField[] = [
    { label: 'Zuletzt bearbeitet', fieldName: 'lastModifiedDate', key: 'lastModifiedDate' },
    { label: 'Erstellt', fieldName: 'createdDate', key: 'createdDate' },
    { label: 'Fälligkeitsdatum', fieldName: 'dueDateTime', key: 'dueDateTime' }
  ];

  readonly globalSearchText = signal<string>('');

  readonly issueTypesOptions = toSignal(this.issueTypeService.getIssueTypes(true), { initialValue: [] });
  readonly issueStatusOptions = toSignal(this.issueStatusService.getIssueStatuses(), { initialValue: [] });
  readonly assigneesOptions = toSignal(this.usersService.getActiveUsers(), { initialValue: [] });
  readonly locationsOptions = toSignal(this.locationService.getLocations(), { initialValue: [] });

  readonly globalSearchInput = viewChild<ElementRef<HTMLInputElement>>('globalSearch');
  readonly tabView = viewChild<TabView>('tabView');

  private readonly unregisterRefetch = this.refetchCoordinator.register(
    this,
    Issue.ENTITY_TYPE
  );

  private _stateRestored = false;

  constructor() {
    effect(() => {
      const key = this.filterStorageKey();
      if (key && !this._stateRestored) {
        this._stateRestored = true;
        const savedState = this.filterStateService.restore<IssueListFilterState>(
          key,
          isValidIssueListFilterState
        );
        if (savedState) {
          untracked(() => this.filterState.set(savedState));
        }
      }
    });

    effect(() => {
      const inputEl = this.globalSearchInput();
      if (inputEl) {
        fromEvent(inputEl.nativeElement, 'input').pipe(
          map((event: Event) => (event.target as HTMLInputElement).value),
          debounceTime(300),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(value => {
          this.globalSearchText.set(value);
          this.pageNumber.set(0);
        });
      }
    });

    effect(() => {
      this.totalElements();
      runInInjectionContext(this.injector, () => {
        afterNextRender(() => this.tabView()?.updateInkBar());
      });
    });

    effect(() => {
      const key = this.filterStorageKey();
      const state = this.filterState();
      untracked(() => {
        if (key) {
          this.filterStateService.save(key, state);
        }
      });
    });
  }

  private readonly container = new ReactiveContainer<fromGraphQl.IssuesQueryResponse>(
    fromGraphQl.ISSUES_QUERY,
    { useGlobalLoading: false,
      pollInterval: 60000,
      stopPollingOnSuccess: false
     }
  );

  readonly data = this.container.fromSignals(
    [this.pageNumber, this.pageSize, this.sortModel,
     this.issueTypesModel, this.issueStatusesModel, this.assigneesModel, this.locationsModel,
     this.binaryStatus, this.targetEntityType, this.targetEntityId, this.globalSearchText, this.dueDateModel, this.labelsModel],
    ([page, size, sortValue, types, statuses, assignees, locations, binaryStatus, entityType, entityId, searchText, dueDate, labels]) => ({
      number: page,
      size: size,
      sortProperty: sortValue.field.fieldName,
      sortDirection: sortValue.direction,
      typeCodes: types.length > 0 ? types : undefined,
      states: statuses.length > 0 ? statuses : undefined,
      assigneeIds: assignees.length > 0 ? assignees : undefined,
      locationIds: locations?.length > 0 ? locations : undefined,
      binaryStatus: binaryStatus,
      inwardEntityType: entityType,
      inwardEntityId: entityId,
      text: searchText || undefined,
      fromDueDateTime: dueDate?.fromDate || undefined,
      toDueDateTime: dueDate?.toDate || undefined,
      includeOverdue: dueDate?.includeOverDue ?? undefined,
      labelIds: labels.length > 0 ? labels : undefined
    })
  );

  readonly issues = this.container.selectWithDefault(
    d => Issue.createIssues(d.issues?.content ?? []),
    []
  );
  readonly totalElements = this.container.selectWithDefault(
    d => d.issues?.totalElements ?? 0,
    0
  );
  readonly labelsOptions = this.container.selectWithDefault(
    d => d.getLabels?.map(l => new Label(l)) ?? [],
    []
  );
  readonly loading = this.container.loading;

  readonly reloadable: Reloadable = this;

  onFilterChange(event: any): void {
    this.filterState.update(s => ({ ...s, tabIndex: event.index }));
    this.pageNumber.set(0);
  }

  reload(): void {
    console.log('IssueListComponent: reload triggered');
    this.container.reload();
  }

  onPageChange(page: number): void {
    this.pageNumber.set(page);
  }

  onPageSizeChange(newPageSize: number): void {
    this.filterState.update(s => ({ ...s, pageSize: newPageSize }));
    this.pageNumber.set(0);
  }

  onSortChange(sortValue: SortValue): void {
    this.filterState.update(s => ({
      ...s,
      sort: { key: sortValue.field.key, direction: sortValue.direction }
    }));
    this.pageNumber.set(0);
  }

  onIssueTypesChange(types: string[]): void {
    this.filterState.update(s => ({ ...s, issueTypeCodes: types }));
    this.pageNumber.set(0);
  }

  onIssueStatusesChange(statuses: string[]): void {
    this.filterState.update(s => ({ ...s, issueStatusCodes: statuses }));
    this.pageNumber.set(0);
  }

  onAssigneesChange(assignees: string[]): void {
    this.filterState.update(s => ({ ...s, assigneeIds: assignees }));
    this.pageNumber.set(0);
  }

  onDueDateChange(dueDate: FilterDateSelectValue | null): void {
    this.filterState.update(s => ({ ...s, dueDate }));
    this.pageNumber.set(0);
  }

  onLabelsChange(labels: string[]): void {
    this.filterState.update(s => ({ ...s, labelIds: labels }));
    this.pageNumber.set(0);
  }

  onLocationsChange(locations: string[]): void {
    this.filterState.update(s => ({ ...s, locationIds: locations }));
    this.pageNumber.set(0);
  }

  ngOnDestroy(): void {
    this.unregisterRefetch();
    if (this.sidebarService.isOpen()) {
      this.sidebarService.close();
    }
  }
}
